<script setup>
/* global axios */
// Config dos nós de mídia (sendImage/sendVideo/sendAudio/sendFile) — upload de arquivo + legenda.
// Mantém a UX do original (botão de upload, prévia, remover) usando o endpoint nativo kanban/uploads.
import { ref, computed, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import FlowField from './FlowField.vue';

const props = defineProps({ node: { type: Object, required: true } });
const { t } = useI18n();
const aux = inject('flowAux', {});
const d = computed(() => props.node.data);
const type = computed(() => props.node.data.nodeType);

const URL_KEY = {
  sendImage: 'imageUrl',
  sendVideo: 'videoUrl',
  sendAudio: 'audioUrl',
  sendFile: 'fileUrl',
};
const ACCEPT = {
  sendImage: 'image/*',
  sendVideo: 'video/*',
  sendAudio: 'audio/*',
  sendFile: '*',
};
// chave i18n do rótulo por tipo de mídia
const LABEL_KEY = {
  sendImage: 'CHATBOT.nodeConfig.media.image',
  sendVideo: 'CHATBOT.nodeConfig.media.video',
  sendAudio: 'CHATBOT.nodeConfig.media.audio',
  sendFile: 'CHATBOT.nodeConfig.media.file',
};
const urlKey = computed(() => URL_KEY[type.value]);
const url = computed(() => d.value[urlKey.value]);

const fileInput = ref(null);
const uploading = ref(false);
const progress = ref(0);

const upload = async e => {
  const file = e.target.files[0];
  if (!file) return;
  uploading.value = true;
  progress.value = 0;
  try {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await axios.post(
      `/api/v1/accounts/${aux.accountId}/kanban/uploads`,
      fd,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: ev => {
          if (ev.total)
            progress.value = Math.round((ev.loaded * 100) / ev.total);
        },
      }
    );
    d.value[urlKey.value] = data.url;
    if (type.value === 'sendFile') d.value.fileName = data.filename;
  } finally {
    uploading.value = false;
    if (fileInput.value) fileInput.value.value = '';
  }
};
const remove = () => {
  d.value[urlKey.value] = '';
  if (type.value === 'sendFile') d.value.fileName = '';
};
</script>

<template>
  <div>
    <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
      t(LABEL_KEY[type])
    }}</label>
    <div v-if="!url" class="mb-3">
      <input
        ref="fileInput"
        type="file"
        :accept="ACCEPT[type]"
        class="text-sm"
        @change="upload"
      />
      <div v-if="uploading" class="mt-2">
        <div class="h-1.5 rounded-full bg-n-alpha-1">
          <div
            class="h-1.5 rounded-full bg-n-brand"
            :style="{ width: progress + '%' }"
          />
        </div>
        <span class="text-[0.6875rem] text-n-slate-10"
          >{{ t('CHATBOT.nodeConfig.media.uploading') }} {{ progress }}%</span
        >
      </div>
    </div>
    <div
      v-else
      class="flex items-center gap-2 p-2 mb-3 border rounded-md border-n-weak bg-n-alpha-1"
    >
      <img
        v-if="type === 'sendImage'"
        :src="url"
        class="object-cover w-12 h-12 rounded"
      />
      <video
        v-else-if="type === 'sendVideo'"
        :src="url"
        class="w-16 h-12 rounded"
      />
      <audio v-else-if="type === 'sendAudio'" :src="url" controls class="h-8" />
      <a
        v-else
        :href="url"
        target="_blank"
        rel="noopener noreferrer"
        class="flex items-center gap-1 flex-1 text-xs text-n-blue-11 truncate hover:underline"
        ><svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          class="w-3.5 h-3.5 shrink-0"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M21.44 11.05l-9.19 9.19a5 5 0 0 1-7.07-7.07l9.19-9.19a3 3 0 0 1 4.24 4.24l-9.2 9.19a1 1 0 0 1-1.41-1.41l8.49-8.49"
          />
        </svg>
        {{ d.fileName || t('CHATBOT.nodeConfig.media.file') }}</a
      >
      <button class="ml-auto text-xs text-n-ruby-11" @click="remove">
        {{ t('CHATBOT.nodeConfig.media.remove') }}
      </button>
    </div>

    <FlowField
      v-if="type !== 'sendAudio'"
      :model-value="d.caption || ''"
      :label="t('CHATBOT.nodeConfig.media.caption')"
      type="text"
      :placeholder="t('CHATBOT.nodeConfig.media.captionPlaceholder')"
      picker
      @update:model-value="d.caption = $event"
    />
    <FlowField
      v-else
      :model-value="d.caption || ''"
      :label="t('CHATBOT.nodeConfig.media.caption')"
      type="text"
      :placeholder="t('CHATBOT.nodeConfig.sendImage.captionLabel')"
      @update:model-value="d.caption = $event"
    />

    <template v-if="type === 'sendAudio'">
      <FlowField
        :model-value="d.asVoice !== false"
        :label="t('CHATBOT.nodeConfig.media.asVoice')"
        type="checkbox"
        @update:model-value="d.asVoice = $event"
      />
      <p class="mt-1 text-[0.6875rem] text-n-slate-10">
        {{ t('CHATBOT.nodeConfig.media.asVoiceHint') }}
      </p>
    </template>
  </div>
</template>
